<template>
  <el-dialog title="选择指定优惠券" :visible="visible" width="780px" append-to-body :close-on-click-modal="false" @close="closePicker">
    <div class="coupon-link-tip">选择允许买家手动领取的优惠券。顾客点击后进入该券的领取页面，尚未生效的券可提前配置，生效后才能领取。</div>
    <form class="coupon-link-toolbar" @submit.prevent="searchCoupons">
      <el-input v-model="keyword" placeholder="搜索优惠券名称" maxlength="100" clearable size="small" @clear="searchCoupons" />
      <el-button type="primary" size="small" native-type="submit">搜索</el-button>
    </form>
    <div v-if="loadError" class="coupon-link-error">优惠券加载失败，<el-button type="text" @click="loadCoupons">点击重试</el-button></div>
    <el-table v-loading="loading" :data="coupons" height="350" empty-text="暂无符合条件的优惠券" @row-click="chooseCoupon">
      <el-table-column width="48">
        <template slot-scope="scope">
          <el-radio :value="pendingCoupon ? String(pendingCoupon.coupon_id) : ''" :label="String(scope.row.coupon_id)"
            :disabled="!!configurationReason(scope.row)" @change="chooseCoupon(scope.row)"><span></span></el-radio>
        </template>
      </el-table-column>
      <el-table-column label="优惠券" min-width="195">
        <template slot-scope="scope"><div>{{ scope.row.title }}</div><div class="coupon-link-id">{{ scope.row.coupon_id }}</div></template>
      </el-table-column>
      <el-table-column label="优惠内容" width="110">
        <template slot-scope="scope">{{ scope.row.discount_type === 'DISCOUNT' ? scope.row.coupon_price + '折' : '¥' + scope.row.coupon_price }}</template>
      </el-table-column>
      <el-table-column label="使用门槛" width="115">
        <template slot-scope="scope">{{ Number(scope.row.coupon_threshold_price || 0) > 0 ? '满' + scope.row.coupon_threshold_price + '元可用' : '无门槛' }}</template>
      </el-table-column>
      <el-table-column label="使用渠道" width="125">
        <template slot-scope="scope">{{ channelName(scope.row.use_channel) }}</template>
      </el-table-column>
      <el-table-column label="领取状态" min-width="150">
        <template slot-scope="scope"><span :class="configurationReason(scope.row) ? 'coupon-link-error' : (unavailableReason(scope.row) ? 'coupon-link-pending' : 'coupon-link-valid')">{{ unavailableReason(scope.row) || '可领取' }}</span></template>
      </el-table-column>
    </el-table>
    <el-pagination class="coupon-link-pagination" layout="total, prev, pager, next" :current-page="pageNo"
      :page-size="pageSize" :total="total" @current-change="changePage" />
    <div v-if="pendingCoupon" class="coupon-link-selection">已选：{{ pendingCoupon.title }}</div>
    <span slot="footer">
      <el-button @click="closePicker">取消</el-button>
      <el-button type="primary" :disabled="!pendingCoupon || !!configurationReason(pendingCoupon) || loading || loadError" @click="confirmCoupon">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getDecorCoupons } from '@/api/coupon'
import { couponUnavailableReason, couponLinkUnavailableReason, normalizeCouponLinkId } from '@/utils/coupon-claim'

export default {
  name: 'CouponLinkPicker',
  props: {
    visible: { type: Boolean, default: false },
    selectedId: { type: [String, Number], default: '' }
  },
  data() {
    return { keyword: '', appliedKeyword: '', pageNo: 1, pageSize: 10, total: 0, coupons: [],
      pendingCoupon: null, loading: false, loadError: false, requestId: 0 }
  },
  watch: {
    visible: {
      immediate: true,
      handler(visible) {
        if (!visible) { this.requestId++; this.loading = false; return }
        this.keyword = ''
        this.appliedKeyword = ''
        this.pageNo = 1
        this.total = 0
        this.coupons = []
        this.pendingCoupon = null
        this.loadCoupons()
      }
    }
  },
  beforeDestroy() { this.requestId++ },
  methods: {
    /** 搜索从第一页开始，翻页使用本次已提交的关键词。 */
    searchCoupons() {
      this.appliedKeyword = this.keyword.trim()
      this.pageNo = 1
      return this.loadCoupons()
    },
    /** 切换候选列表页码，保留已选的单张优惠券。 */
    changePage(page) { this.pageNo = page; return this.loadCoupons() },
    /** 按装修主体读取候选券，隔离搜索、翻页和关闭弹窗后的过期响应。 */
    async loadCoupons() {
      if (!this.visible) return
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = false
      try {
        const response = await getDecorCoupons({ page_no: this.pageNo, page_size: this.pageSize, manual_claim_enabled: true, keyword: this.appliedKeyword || undefined })
        if (requestId !== this.requestId || !this.visible) return
        // 服务端在分页前筛选；兼容旧服务响应时也不展示未开放手动领取的券。
        this.coupons = ((response && response.data) || []).filter(coupon =>
          coupon.manual_claim_enabled === true || Number(coupon.manual_claim_enabled) === 1)
        this.total = Number((response && response.data_total) || 0)
        const selectedId = normalizeCouponLinkId(this.selectedId)
        if (!this.pendingCoupon && selectedId) {
          const selected = this.coupons.find(coupon => normalizeCouponLinkId(coupon.coupon_id) === selectedId)
          if (selected && !this.configurationReason(selected)) this.pendingCoupon = selected
        }
      } catch (error) {
        if (requestId === this.requestId && this.visible) {
          this.loadError = true
          this.coupons = []
          // 保留分页总数，原页重试成功后再更新。
        }
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    /** 允许提前选择未生效券，仍校验手动领取开关及其他配置条件。 */
    chooseCoupon(coupon) {
      if (this.loading || this.loadError || this.configurationReason(coupon)) return
      this.pendingCoupon = coupon
    },
    /** 确认时复查热区配置条件，仅返回单张券，由父组件保存链接。 */
    confirmCoupon() {
      if (this.loading || this.loadError || !this.pendingCoupon || this.configurationReason(this.pendingCoupon)) return
      this.$emit('confirm', this.pendingCoupon)
      this.closePicker()
    },
    /** 关闭候选弹窗并使进行中的加载失效，取消不会保存链接。 */
    closePicker() {
      this.requestId++
      this.loading = false
      this.$emit('update:visible', false)
    },
    /** 校验热区配置条件；未生效仅作为提示，不阻止保存。 */
    configurationReason(coupon) {
      const reason = couponLinkUnavailableReason(coupon)
      if (reason) return reason
      return normalizeCouponLinkId(coupon.coupon_id) ? '' : '优惠券编号无效，请刷新后重试'
    },
    /** 展示当前领取状态，提前配置不会改变真实领取时间。 */
    unavailableReason(coupon) {
      const reason = couponUnavailableReason(coupon)
      if (reason) return reason
      return normalizeCouponLinkId(coupon.coupon_id) ? '' : '优惠券编号无效，请刷新后重试'
    },
    /** 显示优惠券的适用渠道。 */
    channelName(value) { return { ONLINE: '线上专用', OFFLINE: '门店专用', UNIVERSAL: '线上线下通用' }[value] || '未配置渠道' }
  }
}
</script>

<style scoped>
.coupon-link-tip { color: #909399; line-height: 1.7; margin-bottom: 14px; }
.coupon-link-toolbar { display: flex; gap: 12px; margin-bottom: 14px; }
.coupon-link-toolbar .el-input { max-width: 360px; }
.coupon-link-id { color: #909399; font-size: 12px; }
.coupon-link-error { color: #f56c6c; }
.coupon-link-valid { color: #67c23a; }
.coupon-link-pending { color: #e6a23c; }
.coupon-link-pagination { text-align: right; margin-top: 16px; }
.coupon-link-selection { margin-top: 14px; color: #409eff; }
</style>
